import React, { useEffect, useState, useRef } from "react";
import { api, inr } from "@/lib/api";
import { RestaurantLayout } from "@/components/tabdine/RestaurantLayout";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { XCircle } from "lucide-react";

const COLS = [
  { key: "new", label: "New", next: "preparing", cta: "Confirm & Cook" },
  { key: "preparing", label: "Preparing", next: "ready", cta: "Mark Ready" },
  { key: "ready", label: "Ready", next: "served", cta: "Mark Served" },
  { key: "served", label: "Served", next: "paid", cta: "Mark Paid" },
];
const CANCEL_REASONS = [
  "Item out of stock",
  "Kitchen overloaded — cannot fulfil",
  "Customer requested cancellation",
  "Wrong table / duplicate order",
  "Other (see notes)",
];
const CANCELLABLE = new Set(["new", "preparing", "ready"]);

function elapsed(created_at) {
  const m = Math.floor((Date.now() - new Date(created_at).getTime()) / 60000);
  return m < 1 ? "just now" : `${m} min ago`;
}
function ageColor(m) {
  return m < 5 ? "bg-green-100 text-green-800" : m < 15 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800";
}

export default function LiveOrders() {
  const [orders, setOrders] = useState([]);
  const [cancelling, setCancelling] = useState(null); // order being cancelled
  const [reason, setReason] = useState(CANCEL_REASONS[0]);
  const [notes, setNotes] = useState("");
  const [showCancelled, setShowCancelled] = useState(false);
  const lastNewRef = useRef(new Set());

  const load = async () => {
    const r = await api.get("/restaurant/orders");
    const news = r.data.filter((o) => o.status === "new");
    const known = lastNewRef.current;
    const fresh = news.filter((o) => !known.has(o.id));
    if (fresh.length && known.size > 0) {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioCtx();
        const o = ctx.createOscillator();
        o.frequency.value = 880;
        o.connect(ctx.destination);
        o.start();
        setTimeout(() => o.stop(), 120);
      } catch {}
    }
    lastNewRef.current = new Set(news.map((o) => o.id));
    setOrders(r.data);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  const advance = async (o, next) => {
    await api.patch(`/restaurant/orders/${o.id}`, { status: next });
    toast.success(`Order ${o.order_no} → ${next}`);
    load();
  };

  const openCancel = (o) => {
    setCancelling(o);
    setReason(CANCEL_REASONS[0]);
    setNotes("");
  };

  const doCancel = async () => {
    if (!cancelling) return;
    const fullReason = reason === "Other (see notes)" && notes
      ? notes
      : notes ? `${reason} — ${notes}` : reason;
    try {
      await api.patch(`/restaurant/orders/${cancelling.id}`, {
        status: "cancelled",
        cancellation_reason: fullReason,
      });
      toast.success(`Order ${cancelling.order_no} cancelled`);
      setCancelling(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    }
  };

  const grouped = COLS.map((c) => ({ ...c, items: orders.filter((o) => o.status === c.key) }));
  const cancelled = orders.filter((o) => o.status === "cancelled");

  return (
    <RestaurantLayout>
      <div className="p-4 lg:p-6">
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
          <h1 className="font-heading text-2xl font-bold text-brand-dark">Live Orders</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCancelled(!showCancelled)}
              className="text-xs text-brand-dark/60 hover:text-brand-orange underline"
              data-testid="toggle-cancelled"
            >
              {showCancelled ? "Hide" : "Show"} cancelled ({cancelled.length})
            </button>
            <div className="text-xs text-brand-dark/60" data-testid="live-orders-count">{orders.length} total</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {grouped.map((col) => (
            <div key={col.key} className="bg-brand-dark/5 rounded-xl p-3" data-testid={`col-${col.key}`}>
              <div className="text-xs uppercase tracking-widest text-brand-dark/60 font-bold mb-2">
                {col.label} · {col.items.length}
              </div>
              <div className="space-y-2 max-h-[70vh] overflow-y-auto">
                {col.items.length === 0 && (
                  <div className="text-xs text-brand-dark/40 text-center py-6">Nothing here.</div>
                )}
                {col.items.map((o) => {
                  const mins = Math.floor((Date.now() - new Date(o.created_at).getTime()) / 60000);
                  return (
                    <div key={o.id} className="bg-white rounded-lg p-3 border border-brand-dark/5 shadow-sm" data-testid={`order-card-${o.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-heading font-bold text-brand-dark">
                            {o.table_number ? `Table ${o.table_number}` : "Takeaway"}
                          </div>
                          <div className="text-xs text-brand-dark/60">
                            {o.kot_number} · {o.order_no}
                            <span className="ml-1 inline-block bg-brand-orange/10 text-brand-orange px-1.5 py-0.5 rounded text-[10px] uppercase" data-testid={`order-type-badge-${o.id}`}>
                              {o.order_type === "takeaway" ? "Takeaway" : "Dine-In"}
                            </span>
                          </div>
                        </div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${ageColor(mins)}`}>{elapsed(o.created_at)}</span>
                      </div>
                      <div className="mt-2 text-sm space-y-0.5">
                        {o.items.slice(0, 4).map((it, i) => (
                          <div key={i} className="text-brand-dark/80">
                            {it.quantity} × {it.item_name}
                          </div>
                        ))}
                        {o.items.length > 4 && (
                          <div className="text-xs text-brand-dark/50">+{o.items.length - 4} more</div>
                        )}
                      </div>
                      {o.special_instructions && (
                        <div className="mt-2 text-xs bg-brand-orange/10 text-brand-orange p-1.5 rounded">
                          Note: {o.special_instructions}
                        </div>
                      )}
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <div className="text-sm font-heading font-semibold">{inr(o.total_amount)}</div>
                        <div className="flex items-center gap-1">
                          {CANCELLABLE.has(o.status) && (
                            <button
                              onClick={() => openCancel(o)}
                              className="text-xs px-2 py-1 rounded-full text-red-600 hover:bg-red-50 border border-red-200"
                              data-testid={`cancel-${o.id}`}
                              title="Cancel this order"
                            >
                              <XCircle size={12} className="inline mr-0.5" /> Cancel
                            </button>
                          )}
                          {col.next && (
                            <button
                              onClick={() => advance(o, col.next)}
                              data-testid={`advance-${o.id}`}
                              className="text-xs px-2.5 py-1 rounded-full bg-brand-orange text-white hover:bg-brand-orange-dark"
                            >
                              {col.cta} →
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Cancelled orders history */}
        {showCancelled && (
          <div className="mt-6 bg-white rounded-xl border border-brand-dark/5 p-4" data-testid="cancelled-history">
            <div className="font-heading font-semibold text-brand-dark mb-2">Cancelled orders</div>
            {cancelled.length === 0 ? (
              <div className="text-sm text-brand-dark/50">No cancelled orders yet.</div>
            ) : (
              <div className="space-y-2">
                {cancelled.map((o) => (
                  <div key={o.id} className="border border-brand-dark/5 rounded-lg p-3 text-sm flex items-center justify-between gap-2" data-testid={`cancelled-${o.id}`}>
                    <div className="min-w-0">
                      <div className="font-medium text-brand-dark truncate">
                        {o.order_no} · {o.table_number ? `Table ${o.table_number}` : "Takeaway"} · {inr(o.total_amount)}
                      </div>
                      <div className="text-xs text-red-600 mt-0.5">
                        {o.cancellation_reason || "Cancelled"}
                      </div>
                      <div className="text-xs text-brand-dark/50">
                        {o.cancelled_at ? new Date(o.cancelled_at).toLocaleString("en-IN") : ""}
                      </div>
                    </div>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 shrink-0">cancelled</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cancel dialog */}
      <Dialog open={!!cancelling} onOpenChange={(v) => !v && setCancelling(null)}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel order {cancelling?.order_no}</DialogTitle>
            <DialogDescription>Please select a reason. The customer will see this on their order screen.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-brand-dark/60">Reason</label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full h-10 rounded border border-input px-2 text-sm"
                data-testid="cancel-reason"
              >
                {CANCEL_REASONS.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-brand-dark/60">Extra note (optional)</label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Ran out of paneer this evening"
                data-testid="cancel-notes"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setCancelling(null)}>Keep order</Button>
              <Button
                onClick={doCancel}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-cancel"
              >
                Cancel order
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </RestaurantLayout>
  );
}
